/**
 * src/pricingtable/PricingFaq2.jsx — 2026-03-26
 * ------------------------------------------------------------------
 * PURPOSE
 * ------------------------------------------------------------------
 * App-local FAQ section for the new WordPress-aligned pricing page.
 *
 * WHY THIS FILE EXISTS
 * ------------------------------------------------------------------
 * We intentionally keep FAQ content app-local for now because:
 *   • your pricing presentation payload should stay focused on
 *     structured pricing data (plans, features, compare groups)
 *   • your marketing team already manages the FAQ in WordPress via ACF
 *   • we want visual/content parity in-app without expanding the
 *     shared pricing schema prematurely
 *
 * DESIGN GOALS
 * ------------------------------------------------------------------
 * Match the WordPress pricing FAQ section as closely as possible:
 *   • centered section title
 *   • soft neutral background band
 *   • stacked accordion cards
 *   • strong question typography
 *   • Material-style chevron rotation
 *   • multi-paragraph answer support
 *   • inline bold emphasis
 *
 * CONTENT STRATEGY
 * ------------------------------------------------------------------
 * The FAQ content below mirrors the approved pricing-page FAQ copy.
 *
 * We store each answer as JSX so we can preserve:
 *   • paragraph breaks
 *   • inline <strong> emphasis
 *   • simple links if needed later
 *
 * IMPORTANT
 * ------------------------------------------------------------------
 * This file is presentation-only and app-local.
 * It does NOT affect:
 *   • billing logic
 *   • pricing payload generation
 *   • WordPress rendering
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Typography, ButtonBase } from "@mui/material";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/700.css";

/* ------------------------------------------------------------------
 * FAQ content
 * ------------------------------------------------------------------ */

const PRICING_FAQ_ITEMS = [
  {
    id: "getting-started",
    question: "How do teams typically get started with UTM Smart Manager?",
    answer: (
      <>
        <p>
          Most teams start with <strong>Standard</strong> to bring consistency
          to campaign URL creation from day one. That means governed forms,
          reusable templates, naming rules, dependencies, and shared taxonomy
          for their <strong>Google Analytics or Adobe Analytics</strong>{" "}
          implementations.
        </p>

        <p>
          We designed our “Standard” like this on purpose. Instead of putting
          advanced features for the Campaign URL builder behind higher tiers, we
          included the full URL-governance foundation upfront so teams can
          launch with the standards they actually need.
        </p>

        <p>
          When teams want to connect those same campaign standards to creative
          performance, they usually move to <strong>Pro</strong> for governed
          asset management, asset tagging, and asset-level records. That makes
          it easier to see which creative performed best.
        </p>

        <p>
          Organizations that want to extend those same standards across broader
          marketing workflows, shared governance, and advanced integrations
          typically move to <strong>Enterprise</strong>.
        </p>
      </>
    ),
  },
  {
    id: "upgrade-later",
    question: "Can I start with Standard and upgrade later?",
    answer: (
      <>
        <p>
          Yes. UTM Smart Manager is designed to grow with you.
        </p>

        <p>
          Many teams begin with <strong>Standard</strong> to get campaign URL
          governance in place first, then move to <strong>Pro</strong> when
          they want governed asset management and creative performance
          visibility, and later expand to <strong>Enterprise</strong> when they
          need broader workflow standardization, organization-wide governance,
          and advanced integrations.
        </p>

        <p>
          That means you can start with the workflow you need now and expand
          without rebuilding your foundation later.
        </p>
      </>
    ),
  },
  {
    id: "full-url-builder",
    question: "Do I need a higher plan to get the full campaign URL builder?",
    answer: (
      <>
        <p>
          No. <strong>Standard includes the full foundation for governed campaign URL creation.</strong>
        </p>

        <p>
          That includes your <strong>Google Analytics and Adobe Analytics URL builders, unlimited campaign URL forms, unlimited templates, bulk URL creation, unlimited link creation, reusable taxonomy, dependencies, and governance rules</strong>.
        </p>

        <p>
          We structured Standard this way because teams looking for a serious
          campaign URL builder should be able to get the full URL-governance
          system without being pushed into a higher plan first.
        </p>
      </>
    ),
  },
  {
    id: "add-ons-and-limits",
    question: "Are any features or limits billed separately?",
    answer: (
      <>
        <p>
          Yes. Some capabilities are available as <strong>add-ons</strong> or
          have <strong>usage limits</strong> depending on the plan and your
          rollout needs.
        </p>

        <p>
          For example, <strong>Adobe Analytics integration, BigQuery or Looker integrations, and Bitly-related capabilities</strong> may require additional fees depending on your account setup. <strong>Asset management</strong> also starts with an allocation of <strong>2 GB per organization</strong>, and teams that need more storage can purchase additional storage as an add-on.
        </p>

        <p>
          This gives teams a clear way to start with the workflow they need
          today and expand as their governance, reporting, or asset volume
          grows.
        </p>
      </>
    ),
  },
  {
    id: "trial-and-help",
    question:
      "Can I try UTM Smart Manager before committing, and what help is available if I need it?",
    answer: (
      <>
        <p>
          Yes. You can start with a <strong>free trial</strong> and begin
          building right away from a blank form or from a template.
        </p>

        <p>
          UTM Smart Manager was built to be easy for marketers and campaign
          teams to use without heavy setup, so most teams can get started on
          their own. If you need help, you can reach us through chat or by
          email at <strong>support@utmsmartmanager.com</strong>.
        </p>

        <p>
          Customers on paid plans receive the support included with their plan,
          and teams on <strong>Pro</strong> or <strong>Enterprise</strong> can
          also request more hands-on onboarding and concierge-style rollout help
          when needed.
        </p>
      </>
    ),
  },
  {
    id: "support-included",
    question: "What support is included with each plan?",
    answer: (
      <>
        <p>
          <strong>Standard</strong> includes email support for teams getting
          started with governed campaign URL workflows.
        </p>

        <p>
          <strong>Pro</strong> adds implementation support for teams that want
          more rollout help as they expand into asset governance and performance
          visibility.
        </p>

        <p>
          <strong>Enterprise</strong> is built for organizations that need
          higher-touch support, including priority support and help with
          rollout, governance, and integration planning.
        </p>
      </>
    ),
  },
];

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */

function ChevronIcon({ expanded }) {
  return (
    <svg
      viewBox="0 0 24 24"
      focusable="false"
      aria-hidden="true"
      style={{
        width: 20,
        height: 20,
        display: "block",
        fill: "var(--utm-primary, #293189)",
        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform .2s ease, opacity .2s ease",
        opacity: expanded ? 1 : 0.95,
      }}
    >
      <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
    </svg>
  );
}

ChevronIcon.propTypes = {
  expanded: PropTypes.bool.isRequired,
};

/* ------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------ */

const PricingFaq2 = ({ title = "Frequently asked questions" }) => {
  const items = useMemo(() => PRICING_FAQ_ITEMS, []);
  const [expandedId, setExpandedId] = useState(null);

/*
  useEffect(() => {
    console.log("[PricingFaq2] Mounted:", {
      title,
      itemCount: items.length,
      itemIds: items.map((item) => item.id),
    });
  }, [items, title]);

  useEffect(() => {
    console.log("[PricingFaq2] Expanded state update:", {
      expandedId,
    });
  }, [expandedId]);
*/

  const handleToggle = useCallback((id) => {
    setExpandedId((prev) => {
      const next = prev === id ? null : id;

//      console.log("[PricingFaq2] handleToggle:", {
//        previousExpandedId: prev,
//        nextExpandedId: next,
//        toggledId: id,
//      });

      return next;
    });
  }, []);

  return (
    <Box
      component="section"
      aria-labelledby="pricing-faq2-title"
      sx={{
        background: "var(--utm-split, #F5F5F5)",
        padding: "32px 0 40px",
      }}
    >
      <Box
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
          paddingLeft: "2rem",
          paddingRight: "2rem",
        }}
      >
        <Typography
          id="pricing-faq2-title"
          component="h2"
          sx={{
            textAlign: "center",
            fontSize: "clamp(30px, 5vw, 46px)",
            fontWeight: 800,
            letterSpacing: "-0.01em",
            color: "var(--utm-ink, #0F1A2A)",
            margin: "0 0 8px 0",
            fontFamily:
              "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
          }}
        >
          {title}
        </Typography>

        <Box
          role="list"
          sx={{
            display: "grid",
            gap: "12px",
            mt: "16px",
          }}
        >
          {items.map((item) => {
            const expanded = expandedId === item.id;
            const buttonId = `pricing-faq2-${item.id}-button`;
            const panelId = `pricing-faq2-${item.id}-panel`;

            return (
              <Box
                key={item.id}
                role="listitem"
                sx={{
                  background: "#FFFFFF",
                  border: "1px solid rgba(15,26,42,.06)",
                  borderRadius: "14px",
                  boxShadow: "0 10px 24px rgba(15,26,42,.06)",
                  padding: "0 20px",
                }}
              >
                <Typography
                  component="h3"
                  sx={{
                    margin: 0,
                  }}
                >
                  <ButtonBase
                    id={buttonId}
                    onClick={() => handleToggle(item.id)}
                    aria-expanded={expanded ? "true" : "false"}
                    aria-controls={panelId}
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      textAlign: "left",
                      padding: "18px 0",
                      borderRadius: "10px",
                      "&:focus-visible": {
                        outline: "2px solid var(--utm-accent, #4864ff)",
                        outlineOffset: "4px",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: "-0.01em",
                        lineHeight: 1.3,
                        color: "var(--utm-ink, #0F1A2A)",
                        fontSize: "clamp(16px, 2.1vw, 20px)",
                        marginRight: "14px",
                        fontFamily:
                          "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                      }}
                    >
                      {item.question}
                    </Box>

                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 20,
                        height: 20,
                        flex: "0 0 auto",
                      }}
                    >
                      <ChevronIcon expanded={expanded} />
                    </Box>
                  </ButtonBase>
                </Typography>

                <Box
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  hidden={!expanded}
                  sx={{
                    display: expanded ? "block" : "none",
                    padding: "0 0 16px",
                  }}
                >
                  <Box
                    sx={{
                      color: "#334155",
                      lineHeight: 1.65,
                      fontSize: "clamp(15px, 1.9vw, 18px)",
                      maxWidth: "88ch",
                      marginLeft: 0,
                      marginRight: 0,
                      fontFamily:
                        "Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
                      "& p": {
                        margin: 0,
                      },
                      "& p + p": {
                        marginTop: "16px",
                      },
                      "& strong": {
                        color: "var(--utm-ink, #0F1A2A)",
                        fontWeight: 800,
                      },
                      "& a": {
                        color: "var(--utm-cta-blue, #4864ff)",
                        textDecoration: "none",
                        fontWeight: 700,
                      },
                      "& a:hover": {
                        textDecoration: "underline",
                      },
                      "& code": {
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace',
                        background: "#f6f8fa",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        padding: "0.05em 0.35em",
                        fontSize: "0.95em",
                        color: "#0f172a",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    {item.answer}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

PricingFaq2.propTypes = {
  title: PropTypes.string,
};

export default React.memo(PricingFaq2);